import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminImageUpload from '@/components/feature/AdminImageUpload';

export interface ServiceItemData {
  icon: string;
  title: string;
  color: string;
  image: string;
  desc: string;
  benefits: string[];
  forWho: string;
  buttonText?: string;
  buttonLink?: string;
}

export interface FeaturedServiceData {
  badge: string;
  mostPopular: string;
  title: string;
  color: string;
  accentColor: string;
  image: string;
  desc: string;
  benefits: string[];
  forWho: string;
  buttonText?: string;
  buttonLink?: string;
}

export interface ServicesDetailData {
  featured: FeaturedServiceData;
  header: {
    prefix: string;
    highlight: string;
    subtitle: string;
  };
  items: ServiceItemData[];
  footerCta: {
    title: string;
    subtitle: string;
    book: string;
    link?: string;
  };
}

interface ServiceCardProps {
  service: ServiceItemData;
  index: number;
  isEditing?: boolean;
  onChange?: (updated: ServiceItemData) => void;
  onDelete?: () => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  index,
  isEditing = false,
  onChange,
  onDelete,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  const handleBenefitChange = (bIdx: number, val: string) => {
    if (!onChange) return;
    const updated = service.benefits.map((b, i) => (i === bIdx ? val : b));
    onChange({ ...service, benefits: updated });
  };

  const handleAddBenefit = () => {
    if (!onChange) return;
    onChange({ ...service, benefits: [...service.benefits, 'Nuevo beneficio clave'] });
  };

  const handleRemoveBenefit = (bIdx: number) => {
    if (!onChange) return;
    onChange({ ...service, benefits: service.benefits.filter((_, i) => i !== bIdx) });
  };

  const buttonText = service.buttonText || 'Reservar una Consulta';
  const buttonLink = service.buttonLink || '/contact';

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center transition-all duration-700 relative group ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      {/* Delete service button in editing mode */}
      {isEditing && onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute -top-3 right-0 z-20 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg cursor-pointer"
        >
          <i className="ri-delete-bin-line" />
          Eliminar Servicio
        </button>
      )}

      {/* Image */}
      <div className="w-full lg:w-[45%] flex-shrink-0">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-56 md:h-64 object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/70 via-transparent to-transparent" />
          <div
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background: `${service.color}25`, border: `1px solid ${service.color}40`, color: service.color }}
          >
            <i className={`${service.icon} text-sm`} />
            {service.title}
          </div>
        </div>

        {isEditing && (
          <div className="mt-3">
            <AdminImageUpload
              label="Imagen de Servicio"
              value={service.image}
              onChange={(imgUrl) => onChange && onChange({ ...service, image: imgUrl })}
              folder="services"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
          style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}
        >
          <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Título del Servicio</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => onChange && onChange({ ...service, title: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-white font-bold text-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Color HEX (#FF2DAA, etc.)</label>
                <input
                  type="text"
                  value={service.color}
                  onChange={(e) => onChange && onChange({ ...service, color: e.target.value })}
                  className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Descripción</label>
              <textarea
                value={service.desc}
                onChange={(e) => onChange && onChange({ ...service, desc: e.target.value })}
                rows={3}
                className="w-full bg-black/40 border border-purple-500/30 rounded-lg p-2.5 text-white/80 text-sm focus:outline-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Beneficios / Características</label>
                <button
                  type="button"
                  onClick={handleAddBenefit}
                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 cursor-pointer font-medium"
                >
                  <i className="ri-add-line" /> Añadir Beneficio
                </button>
              </div>
              <div className="space-y-2">
                {service.benefits.map((b, bIdx) => (
                  <div key={bIdx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={b}
                      onChange={(e) => handleBenefitChange(bIdx, e.target.value)}
                      className="flex-1 bg-black/40 border border-white/15 rounded px-2.5 py-1 text-white/80 text-xs focus:outline-none focus:border-purple-500"
                      placeholder={`Beneficio ${bIdx + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveBenefit(bIdx)}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded cursor-pointer"
                      title="Eliminar beneficio"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Para quién es</label>
              <input
                type="text"
                value={service.forWho}
                onChange={(e) => onChange && onChange({ ...service, forWho: e.target.value })}
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white/70 text-xs focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="text-[11px] text-purple-400 uppercase tracking-wider block mb-1">Texto del Botón</label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => onChange && onChange({ ...service, buttonText: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-cyan-400 uppercase tracking-wider block mb-1">Enlace del Botón (URL o ruta)</label>
                <input
                  type="text"
                  value={buttonLink}
                  onChange={(e) => onChange && onChange({ ...service, buttonLink: e.target.value })}
                  className="w-full bg-black/40 border border-cyan-500/40 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-xl md:text-2xl font-black text-white mb-3">{service.title}</h2>
            <p className="text-white/55 text-sm leading-relaxed mb-5">{service.desc}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
              {service.benefits.map((b, bIdx) => (
                <div key={bIdx} className="flex items-start gap-2">
                  <div
                    className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                    style={{ background: `${service.color}20` }}
                  >
                    <i className="ri-check-line text-[10px]" style={{ color: service.color }} />
                  </div>
                  <span className="text-white/55 text-xs leading-relaxed">{b}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/3 border border-white/8 rounded-xl p-3.5 mb-5">
              <span className="text-white/40 text-xs uppercase tracking-wider font-bold">Para quién es: </span>
              <span className="text-white/55 text-xs leading-relaxed">{service.forWho}</span>
            </div>

            {buttonLink.startsWith('http') ? (
              <a
                href={buttonLink}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
                style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
              >
                <i className="ri-calendar-line" />
                {buttonText}
              </a>
            ) : (
              <Link
                to={buttonLink}
                className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
                style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
              >
                <i className="ri-calendar-line" />
                {buttonText}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface ServicesDetailProps {
  data: ServicesDetailData;
  isEditing?: boolean;
  onChange?: (data: ServicesDetailData) => void;
}

const ServicesDetail: React.FC<ServicesDetailProps> = ({ data, isEditing = false, onChange }) => {
  const featuredRef = useRef<HTMLDivElement>(null);
  const [featuredVisible, setFeaturedVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setFeaturedVisible(true); },
      { threshold: 0.1 }
    );
    if (featuredRef.current) observer.observe(featuredRef.current);
    return () => observer.disconnect();
  }, []);

  const handleFeaturedChange = (field: keyof FeaturedServiceData, val: any) => {
    if (!onChange) return;
    onChange({
      ...data,
      featured: { ...data.featured, [field]: val },
    });
  };

  const handleFeaturedBenefitChange = (index: number, val: string) => {
    if (!onChange) return;
    const updated = data.featured.benefits.map((b, i) => (i === index ? val : b));
    onChange({
      ...data,
      featured: { ...data.featured, benefits: updated },
    });
  };

  const handleAddFeaturedBenefit = () => {
    if (!onChange) return;
    onChange({
      ...data,
      featured: { ...data.featured, benefits: [...data.featured.benefits, 'Nuevo beneficio destacado'] },
    });
  };

  const handleRemoveFeaturedBenefit = (index: number) => {
    if (!onChange) return;
    onChange({
      ...data,
      featured: { ...data.featured, benefits: data.featured.benefits.filter((_, i) => i !== index) },
    });
  };

  const handleServiceChange = (index: number, updatedItem: ServiceItemData) => {
    if (!onChange) return;
    const updatedList = data.items.map((item, i) => (i === index ? updatedItem : item));
    onChange({ ...data, items: updatedList });
  };

  const handleAddService = () => {
    if (!onChange) return;
    const newService: ServiceItemData = {
      icon: 'ri-sparkling-fill',
      title: 'Nuevo Servicio Digital',
      color: '#8c52ff',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&h=450&fit=crop',
      desc: 'Descripción detallada de la nueva solución digital ofrecida a clientes y empresas.',
      benefits: ['Beneficio clave 1', 'Beneficio clave 2', 'Soporte y asesoría personalizada'],
      forWho: 'Empresas y profesionales que buscan potenciar sus resultados.',
      buttonText: 'Reservar una Consulta',
      buttonLink: '/contact',
    };
    onChange({ ...data, items: [...data.items, newService] });
  };

  const handleDeleteService = (index: number) => {
    if (!onChange) return;
    onChange({ ...data, items: data.items.filter((_, i) => i !== index) });
  };

  const featuredBtnText = data.featured.buttonText || 'Reservar una Consulta';
  const featuredBtnLink = data.featured.buttonLink || '/contact';
  const footerCtaLink = data.footerCta.link || '/contact';

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse,rgba(140,82,255,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* ── FEATURED SERVICE ── */}
        <div className="mb-8 text-center">
          {isEditing ? (
            <input
              type="text"
              value={data.featured.badge}
              onChange={(e) => handleFeaturedChange('badge', e.target.value)}
              className="bg-black/40 border border-[#00c2ff]/40 rounded-full px-4 py-1 text-[#00c2ff] text-xs font-bold uppercase tracking-widest text-center focus:outline-none"
            />
          ) : (
            <span className="inline-flex items-center gap-2 bg-[#00c2ff]/10 border border-[#00c2ff]/30 rounded-full px-4 py-1.5 text-[#00c2ff] text-xs font-bold uppercase tracking-widest">
              <i className="ri-star-line" />
              {data.featured.badge}
            </span>
          )}
        </div>

        <div
          ref={featuredRef}
          className={`relative rounded-3xl overflow-hidden border border-[#00c2ff]/20 mb-28 transition-all duration-1000 ${
            featuredVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ background: 'linear-gradient(135deg, rgba(0,194,255,0.06) 0%, rgba(140,82,255,0.06) 100%)' }}
        >
          {/* Top glow line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00c2ff]/60 to-transparent" />

          <div className="flex flex-col lg:flex-row">
            {/* Image side */}
            <div className="w-full lg:w-1/2 relative overflow-hidden flex flex-col justify-between">
              <div className="relative">
                <img
                  src={data.featured.image}
                  alt={data.featured.title}
                  className="w-full h-72 lg:h-full min-h-[400px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#030006] hidden lg:block" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/80 to-transparent lg:hidden" />
                {/* Badge */}
                <div className="absolute top-5 left-5 flex items-center gap-2 bg-[#00c2ff]/20 border border-[#00c2ff]/40 rounded-full px-4 py-2">
                  <div className="w-2 h-2 rounded-full bg-[#00c2ff] animate-pulse" />
                  {isEditing ? (
                    <input
                      type="text"
                      value={data.featured.mostPopular}
                      onChange={(e) => handleFeaturedChange('mostPopular', e.target.value)}
                      className="bg-transparent text-[#00c2ff] text-xs font-bold uppercase tracking-wider focus:outline-none"
                    />
                  ) : (
                    <span className="text-[#00c2ff] text-xs font-bold uppercase tracking-wider">
                      {data.featured.mostPopular}
                    </span>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="p-4 bg-black/80 border-t border-white/10">
                  <AdminImageUpload
                    label="Imagen Servicio Destacado"
                    value={data.featured.image}
                    onChange={(url) => handleFeaturedChange('image', url)}
                    folder="services"
                  />
                </div>
              )}
            </div>

            {/* Content side */}
            <div className="flex-1 p-8 md:p-12 flex flex-col justify-center">
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
                style={{ background: 'rgba(0,194,255,0.15)', border: '1px solid rgba(0,194,255,0.3)' }}
              >
                <i className="ri-global-line text-2xl text-[#00c2ff]" />
              </div>

              {isEditing ? (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-[11px] text-cyan-400 uppercase tracking-wider block mb-1">Título Destacado</label>
                    <input
                      type="text"
                      value={data.featured.title}
                      onChange={(e) => handleFeaturedChange('title', e.target.value)}
                      className="w-full bg-black/40 border border-[#00c2ff]/40 rounded-lg px-3 py-2 text-white font-black text-2xl focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Descripción</label>
                    <textarea
                      value={data.featured.desc}
                      onChange={(e) => handleFeaturedChange('desc', e.target.value)}
                      rows={3}
                      className="w-full bg-black/40 border border-white/15 rounded-lg p-2.5 text-white/80 text-sm focus:outline-none"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-[11px] text-white/50 uppercase tracking-wider">Beneficios</label>
                      <button
                        type="button"
                        onClick={handleAddFeaturedBenefit}
                        className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 cursor-pointer font-medium"
                      >
                        <i className="ri-add-line" /> Añadir Beneficio
                      </button>
                    </div>
                    <div className="space-y-2">
                      {data.featured.benefits.map((b, bIdx) => (
                        <div key={bIdx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={b}
                            onChange={(e) => handleFeaturedBenefitChange(bIdx, e.target.value)}
                            className="flex-1 bg-black/40 border border-white/15 rounded px-2.5 py-1 text-white/80 text-xs focus:outline-none focus:border-cyan-400"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveFeaturedBenefit(bIdx)}
                            className="p-1 text-red-400 hover:text-red-300 rounded cursor-pointer"
                          >
                            <i className="ri-close-line text-sm" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Para quién es</label>
                    <input
                      type="text"
                      value={data.featured.forWho}
                      onChange={(e) => handleFeaturedChange('forWho', e.target.value)}
                      className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white/70 text-xs focus:outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <div>
                      <label className="text-[11px] text-cyan-400 uppercase tracking-wider block mb-1">Texto del Botón</label>
                      <input
                        type="text"
                        value={featuredBtnText}
                        onChange={(e) => handleFeaturedChange('buttonText', e.target.value)}
                        className="w-full bg-black/40 border border-cyan-500/40 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Enlace del Botón</label>
                      <input
                        type="text"
                        value={featuredBtnLink}
                        onChange={(e) => handleFeaturedChange('buttonLink', e.target.value)}
                        className="w-full bg-black/40 border border-white/20 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
                    {data.featured.title}
                  </h2>
                  <p className="text-white/60 text-base leading-relaxed mb-7">{data.featured.desc}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                    {data.featured.benefits.map((b, bIdx) => (
                      <div key={bIdx} className="flex items-start gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 bg-[#00c2ff]/15">
                          <i className="ri-check-line text-xs text-[#00c2ff]" />
                        </div>
                        <span className="text-white/65 text-sm leading-relaxed">{b}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white/4 border border-white/10 rounded-xl p-4 mb-7">
                    <div className="flex items-center gap-2 mb-1.5">
                      <i className="ri-user-heart-line text-[#00c2ff] text-sm" />
                      <span className="text-white/50 text-xs uppercase tracking-wider font-bold">Para quién es:</span>
                    </div>
                    <p className="text-white/60 text-sm leading-relaxed">{data.featured.forWho}</p>
                  </div>

                  {featuredBtnLink.startsWith('http') ? (
                    <a
                      href={featuredBtnLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-base w-fit"
                      style={{ background: 'linear-gradient(135deg, #00c2ff, #8c52ff)' }}
                    >
                      <i className="ri-calendar-check-line" />
                      {featuredBtnText}
                    </a>
                  ) : (
                    <Link
                      to={featuredBtnLink}
                      className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-base w-fit"
                      style={{ background: 'linear-gradient(135deg, #00c2ff, #8c52ff)' }}
                    >
                      <i className="ri-calendar-check-line" />
                      {featuredBtnText}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── SECTION HEADER ── */}
        <div className="text-center mb-16">
          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3 mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={data.header.prefix}
                  onChange={(e) =>
                    onChange && onChange({ ...data, header: { ...data.header, prefix: e.target.value } })
                  }
                  className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white font-black text-2xl text-right focus:outline-none"
                />
                <input
                  type="text"
                  value={data.header.highlight}
                  onChange={(e) =>
                    onChange && onChange({ ...data, header: { ...data.header, highlight: e.target.value } })
                  }
                  className="flex-1 bg-black/40 border border-pink-500/40 rounded px-3 py-2 text-pink-400 font-black text-2xl text-left focus:outline-none"
                />
              </div>
              <input
                type="text"
                value={data.header.subtitle}
                onChange={(e) =>
                  onChange && onChange({ ...data, header: { ...data.header, subtitle: e.target.value } })
                }
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-1.5 text-white/60 text-sm text-center focus:outline-none"
              />
            </div>
          ) : (
            <>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                {data.header.prefix}{' '}
                <span className="bg-gradient-to-r from-[#FF2DAA] to-[#8c52ff] bg-clip-text text-transparent">
                  {data.header.highlight}
                </span>
              </h2>
              <p className="text-white/50 text-base max-w-xl mx-auto">
                {data.header.subtitle}
              </p>
            </>
          )}

          {/* Add Service button in edit mode */}
          {isEditing && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleAddService}
                className="px-5 py-2.5 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] hover:opacity-90 text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-purple-500/30 cursor-pointer"
              >
                <i className="ri-add-circle-line text-lg" />
                Añadir Nuevo Servicio al Catálogo
              </button>
            </div>
          )}
        </div>

        {/* ── SERVICE CARDS ── */}
        <div className="space-y-20">
          {data.items.map((service, i) => (
            <ServiceCard
              key={i}
              service={service}
              index={i}
              isEditing={isEditing}
              onChange={(updated) => handleServiceChange(i, updated)}
              onDelete={() => handleDeleteService(i)}
            />
          ))}
        </div>

        {/* ── BOTTOM CTA STRIP ── */}
        <div
          className="mt-24 relative rounded-3xl overflow-hidden border border-white/10 p-10 md:p-14 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(140,82,255,0.08) 0%, rgba(0,194,255,0.08) 100%)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8c52ff]/50 to-transparent" />

          {isEditing ? (
            <div className="max-w-lg mx-auto space-y-3 mb-4">
              <input
                type="text"
                value={data.footerCta.title}
                onChange={(e) =>
                  onChange && onChange({ ...data, footerCta: { ...data.footerCta, title: e.target.value } })
                }
                className="w-full bg-black/40 border border-purple-500/40 rounded px-3 py-2 text-white font-bold text-lg text-center focus:outline-none"
              />
              <textarea
                value={data.footerCta.subtitle}
                onChange={(e) =>
                  onChange && onChange({ ...data, footerCta: { ...data.footerCta, subtitle: e.target.value } })
                }
                rows={2}
                className="w-full bg-black/40 border border-white/15 rounded px-3 py-1.5 text-white/70 text-sm text-center focus:outline-none"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={data.footerCta.book}
                  onChange={(e) =>
                    onChange && onChange({ ...data, footerCta: { ...data.footerCta, book: e.target.value } })
                  }
                  className="w-full bg-black/40 border border-cyan-500/40 rounded px-3 py-1.5 text-cyan-300 font-semibold text-xs text-center focus:outline-none"
                  placeholder="Texto del botón"
                />
                <input
                  type="text"
                  value={footerCtaLink}
                  onChange={(e) =>
                    onChange && onChange({ ...data, footerCta: { ...data.footerCta, link: e.target.value } })
                  }
                  className="w-full bg-black/40 border border-white/20 rounded px-3 py-1.5 text-white text-xs text-center focus:outline-none"
                  placeholder="Enlace URL (/contact, etc.)"
                />
              </div>
            </div>
          ) : (
            <>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{data.footerCta.title}</h3>
              <p className="text-white/55 text-base mb-8 max-w-lg mx-auto">
                {data.footerCta.subtitle}
              </p>
              {footerCtaLink.startsWith('http') ? (
                <a
                  href={footerCtaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
                >
                  <i className="ri-chat-1-line" />
                  {data.footerCta.book}
                </a>
              ) : (
                <Link
                  to={footerCtaLink}
                  className="whitespace-nowrap inline-flex items-center gap-2.5 font-bold px-8 py-4 rounded-full bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white hover:opacity-90 hover:scale-105 transition-all duration-300 cursor-pointer text-base"
                >
                  <i className="ri-chat-1-line" />
                  {data.footerCta.book}
                </Link>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesDetail;
